import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'dark');

  useEffect(() => {
    // Check for Google OAuth session_id in URL hash
    const hash = window.location.hash;
    if (hash && hash.includes('session_id=')) {
      const sessionId = hash.split('session_id=')[1]?.split('&')[0];
      if (sessionId) {
        handleGoogleCallback(sessionId);
        return;
      }
    }
    
    if (token) {
      loadUser();
    } else {
      setLoading(false);
    }
  }, []);

  // Separate effect for token changes (not initial load)
  useEffect(() => {
    if (token && !loading) {
      loadUser();
    }
  }, [token]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const handleGoogleCallback = async (sessionId) => {
    try {
      // Exchange session_id for user data
      const response = await axios.get(
        'https://demobackend.emergentagent.com/auth/v1/env/oauth/session-data',
        { headers: { 'X-Session-ID': sessionId } }
      );
      
      const googleUser = response.data;
      
      // Register/login user in our backend
      const backendResponse = await axios.post(`${API}/auth/google`, {
        email: googleUser.email,
        name: googleUser.name,
        picture: googleUser.picture,
        google_id: googleUser.id,
        session_token: googleUser.session_token
      });
      
      const { access_token, user: userData } = backendResponse.data;
      setToken(access_token);
      setUser(userData);
      setTheme(userData.theme || 'dark');
      localStorage.setItem('token', access_token);
      
      // Clean URL
      window.history.replaceState(null, '', window.location.pathname);
    } catch (error) {
      console.error('Google login error:', error);
      setLoading(false);
    }
  };

  const loadUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      setTheme(response.data.theme);
    } catch (error) {
      console.error('Error loading user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const refreshUser = async () => {
    if (token) {
      await loadUser();
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    setToken(access_token);
    setUser(userData);
    setTheme(userData.theme);
    localStorage.setItem('token', access_token);
    return userData;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    const { access_token, user: userData } = response.data;
    setToken(access_token);
    setUser(userData);
    setTheme(userData.theme);
    localStorage.setItem('token', access_token);
    return userData;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
  };

  const toggleTheme = async () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    
    if (token) {
      try {
        await axios.put(`${API}/auth/theme`, { theme: newTheme }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (error) {
        console.error('Error updating theme:', error);
      }
    }
  };

  const getAuthHeader = () => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      theme,
      login,
      register,
      loginWithGoogle,
      logout,
      toggleTheme,
      getAuthHeader,
      refreshUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
